import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  paper: {
    width: "70vw",
    maxWidth: "70vw",
    height: "70vh",
    maxHeight: "70vh",
    display: "flex",
    flexDirection: "column",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: "8px",
    flexShrink: 0,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    padding: "12px 20px 16px",
    gap: "12px",
  },
  // ── Drop zone (merged into imageGrid) ──
  // ── Middle split ──
  middleRow: {
    display: "flex",
    flex: 1,
    gap: "16px",
    overflow: "hidden",
    minHeight: 0,
  },
  leftPanel: {
    width: "40%",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    overflow: "hidden",
  },
  rightPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    overflow: "hidden",
  },
  sectionLabel: {
    fontSize: "11px",
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    flexShrink: 0,
  },
  // ── Image list ──
  imageGrid: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexWrap: "wrap",
    alignContent: "flex-start",
    gap: "8px",
    padding: "8px",
    border: "2px dashed #444",
    borderRadius: "6px",
    background: "#1a1a1a",
    cursor: "pointer",
    transition: "border-color 0.2s, background 0.2s",
    "&:hover": { borderColor: "#777", background: "#222" },
    outline: "none",
  },
  imageGridActive: {
    borderColor: "#90caf9 !important",
    background: "#1a2a3a !important",
  },
  imageThumb: {
    position: "relative",
    width: "72px",
    height: "72px",
    border: "1px solid #444",
    borderRadius: "4px",
    overflow: "hidden",
    background: "#111",
    flexShrink: 0,
  },
  imageThumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  removeBtn: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: "2px",
    background: "rgba(0,0,0,0.75)",
    borderRadius: "0 3px 0 3px",
    color: "#fff",
    "&:hover": { background: "rgba(180,40,40,0.9)" },
  },
  imageName: {
    fontSize: "8px",
    color: "#bbb",
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    textAlign: "center",
    background: "rgba(0,0,0,0.65)",
    padding: "2px 1px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  // ── Padding row ──
  paddingRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexShrink: 0,
  },
  paddingInput: {
    width: "56px",
    padding: "5px 8px",
    background: "#2a2a2a",
    border: "1px solid #555",
    borderRadius: "4px",
    color: "#fff",
    fontSize: "13px",
    textAlign: "center",
  },
  // ── Section header row ──
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    height: "28px",
  },
  // ── Preview ──
  previewBox: {
    flex: 1,
    border: "1px solid #333",
    borderRadius: "4px",
    // checkerboard transparent background
    backgroundImage: [
      "linear-gradient(45deg, #2a2a2a 25%, transparent 25%)",
      "linear-gradient(-45deg, #2a2a2a 25%, transparent 25%)",
      "linear-gradient(45deg, transparent 75%, #2a2a2a 75%)",
      "linear-gradient(-45deg, transparent 75%, #2a2a2a 75%)",
    ].join(", "),
    backgroundSize: "16px 16px",
    backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
    backgroundColor: "#1a1a1a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative",
  },
  previewImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    imageRendering: "pixelated",
  },
  // ── Action bar ──
  actionRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexShrink: 0,
    paddingTop: "10px",
    borderTop: "1px solid #333",
  },
  manualBtn: {
    color: "#888",
    borderColor: "#444",
    fontSize: "12px",
    marginRight: "auto",
  },
  emptyHint: {
    color: "#444",
    fontSize: "13px",
    textAlign: "center",
  },
  sheetInfo: {
    fontSize: "11px",
    color: "#888",
  },
  // ── Sort toggle ──
  sortGroup: {
    display: "flex",
    gap: "3px",
  },
  sortBtn: {
    fontSize: "9px",
    padding: "2px 7px",
    minWidth: 0,
    border: "1px solid #444",
    borderRadius: "3px",
    color: "#666",
    cursor: "pointer",
    background: "transparent",
    letterSpacing: "0.3px",
    lineHeight: "1.4",
    "&:hover": { borderColor: "#888", color: "#aaa" },
  },
  sortBtnActive: {
    borderColor: "#90caf9 !important",
    color: "#90caf9 !important",
  },
}));

const MAX_DIM = 4096;

// ─── MaxRects bin packing ─────────────────────────────────────────────────────
// Industry-standard algorithm (used by TexturePacker, libgdx, etc.)
// Fills gaps next to tall images with smaller ones instead of wasting space.

function rectsOverlap(a, b) {
  return !(b.x >= a.x + a.w || b.x + b.w <= a.x || b.y >= a.y + a.h || b.y + b.h <= a.y);
}
function containsRect(outer, inner) {
  return (
    outer.x <= inner.x && outer.y <= inner.y &&
    outer.x + outer.w >= inner.x + inner.w &&
    outer.y + outer.h >= inner.y + inner.h
  );
}

function tryMaxRects(sorted, sheetW, padding) {
  const pad2 = padding * 2;
  // One giant free rect covering full width; height unbounded (we'll measure used height)
  let freeRects = [{ x: 0, y: 0, w: sheetW, h: 65536 }];
  const frames = [];

  for (const img of sorted) {
    const fw = img.w + pad2;
    const fh = img.h + pad2;

    // Best Short Side Fit: minimise the shorter leftover side
    let bestRect = null;
    let bestScore = Infinity;
    for (const r of freeRects) {
      if (r.w >= fw && r.h >= fh) {
        const score = Math.min(r.w - fw, r.h - fh);
        if (score < bestScore) { bestScore = score; bestRect = r; }
      }
    }
    if (!bestRect) return null; // image doesn't fit at this width

    const px = bestRect.x + padding;
    const py = bestRect.y + padding;
    frames.push({ name: img.name, x: px, y: py, w: img.w, h: img.h, imgElement: img.element, url: img.objectURL });

    // Guillotine-split every free rect that overlaps the placed area
    const placed = { x: bestRect.x, y: bestRect.y, w: fw, h: fh };
    const next = [];
    for (const r of freeRects) {
      if (!rectsOverlap(r, placed)) { next.push(r); continue; }
      if (r.x + r.w > placed.x + placed.w) next.push({ x: placed.x + placed.w, y: r.y, w: r.x + r.w - placed.x - placed.w, h: r.h });
      if (placed.x > r.x) next.push({ x: r.x, y: r.y, w: placed.x - r.x, h: r.h });
      if (r.y + r.h > placed.y + placed.h) next.push({ x: r.x, y: placed.y + placed.h, w: r.w, h: r.y + r.h - placed.y - placed.h });
      if (placed.y > r.y) next.push({ x: r.x, y: r.y, w: r.w, h: placed.y - r.y });
    }
    // Remove rects fully contained by another (dedup)
    freeRects = next.filter((r, i) => !next.some((o, j) => i !== j && containsRect(o, r)));
  }

  const usedW = frames.reduce((m, f) => Math.max(m, f.x + f.w + padding), 0);
  const usedH = frames.reduce((m, f) => Math.max(m, f.y + f.h + padding), 0);
  return { frames, usedW, usedH };
}

function packImages(images, padding) {
  if (images.length === 0) return { frames: [], sheetW: 0, sheetH: 0 };
  const pad2 = padding * 2;
  // Sort by area descending so large images anchor first
  const sorted = [...images].sort((a, b) => b.w * b.h - a.w * a.h);

  const minW = Math.max(...sorted.map((i) => i.w + pad2));
  const maxW = Math.max(MAX_DIM, minW);
  const totalArea = sorted.reduce((s, i) => s + (i.w + pad2) * (i.h + pad2), 0);

  // Build candidate widths: every cumulative sum of sorted image widths (natural breakpoints)
  const candidateWidths = new Set();
  let cum = 0;
  for (const img of sorted) {
    cum += img.w + pad2;
    if (cum >= minW && cum <= maxW) candidateWidths.add(cum);
  }
  // Also sprinkle in geometric steps from minW upward so we don't miss wide layouts
  for (let w = minW; w <= maxW; w = Math.ceil(w * 1.15) + 1) candidateWidths.add(Math.min(w, maxW));

  let best = null;
  let bestScore = Infinity;

  for (const w of [...candidateWidths].sort((a, b) => a - b)) {
    const result = tryMaxRects(sorted, w, padding);
    if (!result) continue;
    const { usedW, usedH } = result;
    // Score: closeness to square, with a small area penalty to prefer compact layouts
    const ratio = Math.max(usedW / usedH, usedH / usedW);
    const area = usedW * usedH;
    const score = ratio + area / (totalArea * 4); // area term breaks ties
    if (score < bestScore) { bestScore = score; best = { frames: result.frames, sheetW: usedW, sheetH: usedH }; }
  }

  return best;
}

// ─── JSON Hash ────────────────────────────────────────────────────────────────

function buildJsonHash(frames, sheetW, sheetH, sheetName, frameRate) {
  const framesObj = {};
  frames.forEach(({ name, x, y, w, h }) => {
    framesObj[name] = {
      frame: { x, y, w, h },
      rotated: false,
      trimmed: false,
      spriteSourceSize: { x: 0, y: 0, w, h },
      sourceSize: { w, h },
      pivot: { x: 0.5, y: 0.5 },
    };
  });
  const meta = {
    app: "PlayinDashboard SpritePacker",
    version: "1.0",
    image: sheetName,
    format: "RGBA8888",
    size: { w: sheetW, h: sheetH },
    scale: "1",
  };
  if (typeof frameRate === "number" && frameRate > 0) meta.frameRate = frameRate;
  return { frames: framesObj, meta };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * @param props.open           {boolean}
 * @param props.onClose        {function}
 * @param props.onPacked       {function({ pngFile, jsonFile })}
 * @param props.onManualUpload {function}
 * @param props.showUpload     {boolean} show the "Pack & Upload" + "I already have a sprite sheet" actions (default true)
 * @param props.showDownload   {boolean} show the "Download" action (default true)
 */
export default function DialogSpritePacker(props) {
  const classes = useStyles();
  const {
    open,
    onClose,
    onPacked,
    onManualUpload,
    isUploading: isUploadingProp,
    showUpload = true,
    showDownload = true,
  } = props;

  const [images, setImages] = useState([]);       // { name, w, h, element, objectURL }
  const [padding, setPadding] = useState(1);
  const [isPacking, setIsPacking] = useState(false);
  const [previewDataURL, setPreviewDataURL] = useState(null);  // rendered sheet as dataURL
  const [packedResult, setPackedResult] = useState(null);      // { frames, sheetW, sheetH, canvas }
  const [isDragOver, setIsDragOver] = useState(false);
  const [sortMode, setSortMode] = useState("upload"); // "upload" | "name"
  const [sheetFileName, setSheetFileName] = useState("spritesheet");
  const [warning, setWarning] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [oversizedDialogOpen, setOversizedDialogOpen] = useState(false);
  const [frameNames, setFrameNames] = useState({});
  const [selectedURL, setSelectedURL] = useState(null);
  const [scalePercent, setScalePercent] = useState(0);
  const [scaleInput, setScaleInput] = useState("0");
  const [fps, setFps] = useState(20);
  const [isPlaying, setIsPlaying] = useState(false);
  const fileInputRef = useRef(null);
  const imageGridRef = useRef(null);
  const playCanvasRef = useRef(null);
  const playIndexRef = useRef(0);
  const playTimerRef = useRef(null);
  const displayImagesRef = useRef([]);

  const showPlayback = !showUpload;

  const namedImages = images.map((i) => ({ ...i, name: frameNames[i.objectURL] || i.name }));
  const displayImages =
    sortMode === "name"
      ? [...namedImages].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
      )
      : namedImages;
  displayImagesRef.current = displayImages;
  const selectedImage = selectedURL ? namedImages.find((i) => i.objectURL === selectedURL) : null;

  useEffect(() => {
    if (open) {
      setImages([]);
      setPadding(1);
      setPackedResult(null);
      setPreviewDataURL(null);
      setSheetFileName("spritesheet");
      setWarning(null);
      setFrameNames({});
      setSelectedURL(null);
      setScalePercent(0);
      setScaleInput("0");
      setFps(20);
      setIsPlaying(false);
      playIndexRef.current = 0;
    }
  }, [open]);

  useEffect(() => {
    if (playTimerRef.current) {
      clearInterval(playTimerRef.current);
      playTimerRef.current = null;
    }
    if (!isPlaying) return;

    const safeFps = Math.max(1, Math.min(60, fps || 1));
    const draw = () => {
      const canvas = playCanvasRef.current;
      const frames = displayImagesRef.current;
      if (!canvas || frames.length === 0) return;
      const item = frames[playIndexRef.current % frames.length];
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (item && item.element) {
        const scale = Math.min(canvas.width / item.element.naturalWidth, canvas.height / item.element.naturalHeight);
        const dw = item.element.naturalWidth * scale;
        const dh = item.element.naturalHeight * scale;
        ctx.drawImage(item.element, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
      }
      playIndexRef.current = (playIndexRef.current + 1) % frames.length;
    };
    draw();
    playTimerRef.current = setInterval(draw, 1000 / safeFps);
    return () => {
      if (playTimerRef.current) {
        clearInterval(playTimerRef.current);
        playTimerRef.current = null;
      }
    };
  }, [isPlaying, fps]);

  useEffect(() => {
    if (isPlaying && displayImages.length === 0) setIsPlaying(false);
  }, [isPlaying, displayImages.length]);

  useEffect(() => {
    if (images.length > 0) {
      runPack(images, padding, scalePercent);
    } else {
      setPackedResult(null);
      setPreviewDataURL(null);
      setWarning(null);
    }
  }, [images, padding, scalePercent]);

  function loadImageFiles(files) {
    const imgFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (imgFiles.length === 0) return;
    const promises = imgFiles.map(
      (file) =>
        new Promise((resolve) => {
          const objectURL = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => resolve({ name: file.name, w: img.naturalWidth, h: img.naturalHeight, element: img, objectURL });
          img.onerror = () => { URL.revokeObjectURL(objectURL); resolve(null); };
          img.src = objectURL;
        })
    );
    Promise.all(promises).then((loaded) => {
      const valid = loaded.filter(Boolean);
      const fitting = valid.filter((i) => i.w <= MAX_DIM && i.h <= MAX_DIM);
      const tooBig = valid.filter((i) => i.w > MAX_DIM || i.h > MAX_DIM);
      if (tooBig.length) {
        tooBig.forEach((i) => URL.revokeObjectURL(i.objectURL));
        setWarning(
          `Skipped ${tooBig.length} image(s) larger than ${MAX_DIM}px: ${tooBig.map((i) => i.name).join(", ")}`
        );
      } else {
        setWarning(null);
      }
      setImages((prev) => {
        const existingNames = new Set(prev.map((i) => i.name));
        return [...prev, ...fitting.filter((i) => !existingNames.has(i.name))];
      });
    });
  }

  function removeImage(objectURL) {
    setImages((prev) => {
      const removed = prev.find((i) => i.objectURL === objectURL);
      if (removed) URL.revokeObjectURL(removed.objectURL);
      return prev.filter((i) => i.objectURL !== objectURL);
    });
    setFrameNames((prev) => {
      const next = { ...prev };
      delete next[objectURL];
      return next;
    });
    if (selectedURL === objectURL) setSelectedURL(null);
  }

  function renameSelected(newName) {
    if (!selectedURL) return;
    setFrameNames((prev) => ({ ...prev, [selectedURL]: newName }));
  }

  function commitScale() {
    let n = parseInt(scaleInput, 10);
    if (isNaN(n)) n = 0;
    n = Math.max(0, Math.min(99, n));
    setScaleInput(String(n));
    setScalePercent(n);
  }

  function runPack(imgs, pad, scalePct) {
    setIsPacking(true);
    setTimeout(() => {
      try {
        const scale = (100 - Math.max(0, Math.min(99, scalePct || 0))) / 100;
        const scaledImgs = imgs.map((i) => ({
          ...i,
          w: Math.max(1, Math.round(i.w * scale)),
          h: Math.max(1, Math.round(i.h * scale)),
        }));
        const packed = packImages(scaledImgs, pad);
        if (!packed) {
          setPackedResult(null);
          setPreviewDataURL(null);
          setWarning("Could not pack these images into a single sheet.");
          return;
        }
        const { frames, sheetW, sheetH } = packed;
        const canvas = document.createElement("canvas");
        canvas.width = sheetW;
        canvas.height = sheetH;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, sheetW, sheetH);
        frames.forEach(({ imgElement, x, y, w, h }) => {
          ctx.drawImage(imgElement, x, y, w, h);
        });
        setPackedResult({ frames, sheetW, sheetH, canvas });
        setPreviewDataURL(canvas.toDataURL("image/png"));
      } catch (err) {
        console.error("[SpritePacker] Pack error:", err);
      } finally {
        setIsPacking(false);
      }
    }, 0);
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    loadImageFiles(e.dataTransfer.files);
  }, []);

  function clearAll() {
    setImages((prev) => { prev.forEach((i) => URL.revokeObjectURL(i.objectURL)); return []; });
    setFrameNames({});
    setSelectedURL(null);
    setPackedResult(null);
    setPreviewDataURL(null);
    setWarning(null);
    setScalePercent(100);
    setScaleInput("100");
  }

  function buildJsonFile() {
    const { frames, sheetW, sheetH } = packedResult;
    const baseName = sheetFileName.trim() || "spritesheet";
    const sheetName = `${baseName}.png`;
    const frameByURL = new Map(frames.map((f) => [f.url, f]));
    const orderedFrames = displayImages
      .map((img) => {
        const f = frameByURL.get(img.objectURL);
        return f ? { ...f, name: img.name } : null;
      })
      .filter(Boolean);
    const frameRate = showPlayback ? Math.max(1, Math.min(60, fps || 1)) : undefined;
    return new File(
      [JSON.stringify(buildJsonHash(orderedFrames, sheetW, sheetH, sheetName, frameRate), null, 2)],
      `${baseName}.json`,
      { type: "application/json" }
    );
  }

  function triggerDownload(file) {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleUpload() {
    if (!canUpload) return;
    if (isOversized) { setOversizedDialogOpen(true); return; }
    const { canvas } = packedResult;
    const baseName = sheetFileName.trim() || "spritesheet";
    canvas.toBlob((blob) => {
      const pngFile = new File([blob], `${baseName}.png`, { type: "image/png" });
      if (onPacked) onPacked({ pngFile, jsonFile: buildJsonFile() });
    }, "image/png");
  }

  function handleDownload() {
    if (!canDownload) return;
    if (isOversized) { setOversizedDialogOpen(true); return; }
    setIsDownloading(true);
    const { canvas } = packedResult;
    const baseName = sheetFileName.trim() || "spritesheet";
    const jsonFile = buildJsonFile();
    canvas.toBlob((blob) => {
      triggerDownload(new File([blob], `${baseName}.png`, { type: "image/png" }));
      triggerDownload(jsonFile);
      setIsDownloading(false);
    }, "image/png");
  }

  const isOversized =
    !!packedResult && (packedResult.sheetW > MAX_DIM || packedResult.sheetH > MAX_DIM);
  const isReady = !!packedResult && !isPacking && images.length > 0;
  const canUpload = isReady;
  const canDownload = isReady && !isDownloading;

  return (
    <Dialog
      open={open}
      onClose={isUploadingProp ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography>
        <div className={classes.titleRow}>
          <Typography variant="h6">Sprite Packer</Typography>
          <IconButton size="small" onClick={onClose} disabled={isUploadingProp}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>

        {/* hidden file input — opened by clicking the image grid */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          hidden
          onChange={(e) => loadImageFiles(e.target.files)}
        />

        {/* Middle: left = images (droppable), right = preview */}
        <div className={classes.middleRow}>

          {/* Left panel — image list / drop target */}
          <div className={classes.leftPanel}>
            {/* Panel header — same height as right panel header */}
            <div className={classes.panelHeader}>
              <Typography className={classes.sectionLabel}>
                Added images ({images.length})
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {images.length > 1 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <span style={{ fontSize: "9px", color: "#555", letterSpacing: "0.3px", textTransform: "uppercase" }}>Sorting:</span>
                    <div className={classes.sortGroup}>
                      <button
                        className={`${classes.sortBtn} ${sortMode === "upload" ? classes.sortBtnActive : ""}`}
                        onClick={() => setSortMode("upload")}
                      >
                        Load order
                      </button>
                      <button
                        className={`${classes.sortBtn} ${sortMode === "name" ? classes.sortBtnActive : ""}`}
                        onClick={() => setSortMode("name")}
                      >
                        Name
                      </button>
                    </div>
                  </div>
                )}
                {images.length > 0 && (
                  <Button variant="outlined" size="small"
                    style={{ fontSize: "11px", color: "#e57373", borderColor: "#e57373", padding: "2px 8px" }}
                    onClick={clearAll}>
                    Clear all
                  </Button>
                )}
              </div>
            </div>
            <div
              ref={imageGridRef}
              className={`${classes.imageGrid} ${isDragOver ? classes.imageGridActive : ""}`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={(e) => { if (!imageGridRef.current?.contains(e.relatedTarget)) setIsDragOver(false); }}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              {images.length === 0 && (
                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", pointerEvents: "none" }}>
                  <Typography style={{ color: "#555", fontSize: "13px" }}>Drop images here</Typography>
                  <Typography style={{ color: "#3a3a3a", fontSize: "11px" }}>or click to select — PNG, JPG, WebP</Typography>
                </div>
              )}
              {displayImages.map((img) => (
                <div
                  key={img.objectURL}
                  className={classes.imageThumb}
                  style={selectedURL === img.objectURL ? { borderColor: "#90caf9", boxShadow: "0 0 0 1px #90caf9" } : undefined}
                  onClick={(e) => { e.stopPropagation(); setSelectedURL(img.objectURL); }}
                >
                  <img src={img.objectURL} alt={img.name} className={classes.imageThumbImg} />
                  <Tooltip title={`${img.name}  (${img.w}×${img.h})`} placement="top">
                    <span className={classes.imageName}>{img.name}</span>
                  </Tooltip>
                  <IconButton
                    className={classes.removeBtn}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); removeImage(img.objectURL); }}
                  >
                    <CloseIcon style={{ fontSize: 11 }} />
                  </IconButton>
                </div>
              ))}
            </div>
            {selectedImage && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                <Typography variant="caption" style={{ color: "#888", fontSize: "11px", whiteSpace: "nowrap" }}>Frame name</Typography>
                <input
                  type="text"
                  value={selectedImage.name}
                  onChange={(e) => renameSelected(e.target.value)}
                  className={classes.paddingInput}
                  style={{ flex: 1, textAlign: "left" }}
                />
              </div>
            )}
          </div>

          {/* Right panel — padding + preview */}
          <div className={classes.rightPanel}>

            {/* Panel header — same height as left panel header */}
            <div className={classes.panelHeader}>
              <div className={classes.paddingRow}>
                <Typography variant="body2" style={{ color: "#ccc", fontSize: "12px" }}>Padding</Typography>
                <input
                  type="number"
                  min={0}
                  max={16}
                  value={padding}
                  className={classes.paddingInput}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setPadding(Math.max(0, Math.min(16, parseInt(e.target.value, 10) || 0)))}
                />
                <Typography variant="caption" style={{ color: "#888" }}>px</Typography>
                <Typography variant="body2" style={{ color: "#ccc", fontSize: "12px", marginLeft: "12px" }}>Resize images</Typography>
                <input
                  type="number"
                  min={0}
                  max={99}
                  value={scaleInput}
                  className={classes.paddingInput}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setScaleInput(e.target.value)}
                  onBlur={commitScale}
                  onKeyDown={(e) => { if (e.key === "Enter") { commitScale(); e.target.blur(); } }}
                />
                <Typography variant="caption" style={{ color: "#888" }}>% smaller</Typography>
              </div>
              {packedResult && !isPacking && (
                <Typography
                  className={classes.sheetInfo}
                  style={isOversized ? { color: "#e57373" } : undefined}
                >
                  {packedResult.sheetW}×{packedResult.sheetH}px &nbsp;·&nbsp; {packedResult.frames.length} frames
                </Typography>
              )}
            </div>

            {showPlayback && (
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!isReady}
                  onClick={() => { playIndexRef.current = 0; setIsPlaying((p) => !p); }}
                  style={{ fontSize: "12px", minWidth: "72px" }}
                >
                  {isPlaying ? "Stop" : "Play"}
                </Button>
                <Typography variant="body2" style={{ color: "#ccc", fontSize: "12px" }}>FPS</Typography>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={fps}
                  className={classes.paddingInput}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setFps(Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 1)))}
                />
                <Typography variant="caption" style={{ color: "#888" }}>
                  baked into downloaded .json
                </Typography>
              </div>
            )}

            {/* Preview */}
            <div className={classes.previewBox}>
              {isPacking && <CircularProgress size={28} color="secondary" />}
              {!isPacking && !previewDataURL && (
                <Typography className={classes.emptyHint}>Add images to see preview</Typography>
              )}
              {!isPacking && previewDataURL && showPlayback && isPlaying && (
                <canvas
                  ref={playCanvasRef}
                  width={512}
                  height={512}
                  className={classes.previewImg}
                  style={{ imageRendering: "pixelated" }}
                />
              )}
              {!isPacking && previewDataURL && !(showPlayback && isPlaying) && (
                <img
                  src={previewDataURL}
                  alt="sprite sheet preview"
                  className={classes.previewImg}
                />
              )}
            </div>

            {warning && (
              <Typography
                variant="caption"
                style={{ color: "#e57373", flexShrink: 0 }}
              >
                {warning}
              </Typography>
            )}

          </div>
        </div>

        {/* Action bar */}
        <div className={classes.actionRow}>
          {showUpload && (
            <Button
              variant="outlined"
              size="small"
              className={classes.manualBtn}
              disabled={isUploadingProp}
              onClick={() => { onClose(); if (onManualUpload) onManualUpload(); }}
            >
              I already have a sprite sheet →
            </Button>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto", marginRight: "6px" }}>
            <Typography variant="caption" style={{ color: "#888", fontSize: "11px", whiteSpace: "nowrap" }}>File name</Typography>
            <input
              type="text"
              value={sheetFileName}
              onChange={(e) => setSheetFileName(e.target.value)}
              placeholder="spritesheet"
              className={classes.paddingInput}
              style={{ width: "130px", textAlign: "left" }}
            />
            <Typography variant="caption" style={{ color: "#555", fontSize: "11px" }}>.png / .json</Typography>
          </div>
          <Button variant="outlined" size="small" onClick={onClose} disabled={isUploadingProp}>
            Cancel
          </Button>
          {showDownload && (
            <Button
              variant={showUpload ? "outlined" : "contained"}
              color="primary"
              size="small"
              style={{ marginRight: 4 }}
              disabled={!canDownload || isUploadingProp}
              onClick={handleDownload}
            >
              {isDownloading ? <><CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} />Preparing...</> : (showUpload ? "Download" : "Pack & Download")}
            </Button>
          )}
          {showUpload && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!canUpload || isUploadingProp}
              onClick={handleUpload}
            >
              {isUploadingProp ? <><CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} />Uploading...</> : "Pack & Upload"}
            </Button>
          )}
        </div>

      </DialogContent>

      <Dialog open={oversizedDialogOpen} onClose={() => setOversizedDialogOpen(false)} maxWidth="sm">
        <DialogTitle disableTypography>
          <Typography variant="h6" style={{ color: "#e57373" }}>Sprite sheet too large</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingBottom: 24, minWidth: 380 }}>
          <Typography style={{ fontSize: 16, marginBottom: 10 }}>
            The packed sheet is {packedResult ? `${packedResult.sheetW}×${packedResult.sheetH}px` : ""}, which exceeds the {MAX_DIM}px limit.
          </Typography>
          <Typography variant="body2" style={{ color: "#aaa", marginBottom: 20 }}>
            Reduce the number of frames or use smaller source images so both width and height stay within {MAX_DIM}px.
          </Typography>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" color="primary" onClick={() => setOversizedDialogOpen(false)}>OK</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
